/**
 * Filters for assessments.
 */
import * as React from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/forms/Button';
import { Select } from '@/components/forms/Select';
import { SearchInput } from '@/components/forms/SearchInput';
import { Autocomplete } from '@/components/forms/Autocomplete';
import { useEstablishmentsAutocomplete } from '@/hooks/useEstablishmentsAutocomplete';
import {
  ASSESSMENT_STATUS_CONFIG,
  ASSESSMENT_STATUS_FILTER_KEYS,
  ASSESSMENT_TYPE_OPTIONS,
} from './assessment-configs';
import type { AssessmentFilters } from '@/types/assessment.types';

/** Props for {@link AssessmentFiltersContent}. */
interface AssessmentFiltersContentProps {
  filters: AssessmentFilters;
  allowedFilterIds: string[];
  visibleFilterIds: string[];
  onSetType: (type: AssessmentFilters['type']) => void;
  onSetStatus: (status: AssessmentFilters['status']) => void;
  onSetEstablishment: (id: string | undefined) => void;
  onSetSearch: (value: string | undefined) => void;
}

/** Filter group is allowed by RBAC and currently toggled on in the configure panel. */
function isVisible(
  filterId: string,
  allowedFilterIds: string[],
  visibleFilterIds: string[],
): boolean {
  return allowedFilterIds.includes(filterId) && visibleFilterIds.includes(filterId);
}

/** Single type select; empty value means all types. */
const TypeFilter: React.FC<{
  filters: AssessmentFilters;
  onSetType: (type: AssessmentFilters['type']) => void;
}> = ({ filters, onSetType }) => (
  <div className="filter-group">
    <span className="filter-group-label">Type de bilan</span>
    <div className="w-56">
      <Select
        placeholder="Tous les types"
        options={[{ value: '', label: 'Tous les types' }, ...ASSESSMENT_TYPE_OPTIONS]}
        value={filters.type ?? ''}
        onChange={(val) => onSetType((val || undefined) as AssessmentFilters['type'])}
        size="sm"
      />
    </div>
  </div>
);

/** Exclusive status chips — clicking the active chip clears the filter. */
const StatusFilter: React.FC<{
  filters: AssessmentFilters;
  onSetStatus: (status: AssessmentFilters['status']) => void;
}> = ({ filters, onSetStatus }) => (
  <div className="filter-group">
    <span className="filter-group-label">Statut</span>
    <div className="filter-options">
      {ASSESSMENT_STATUS_FILTER_KEYS.map((key) => {
        const config = ASSESSMENT_STATUS_CONFIG[key];
        const Icon = config.icon;
        const isActive = filters.status === key;
        return (
          <Button
            key={key}
            variant="custom"
            size="sm"
            icon={Icon}
            iconPosition="left"
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all',
              isActive ? 'filter-badge-active' : 'filter-badge-inactive',
            )}
            style={
              {
                '--filter-color': config.badgeColor ?? 'rgb(var(--status-success))',
              } as React.CSSProperties
            }
            onClick={() => onSetStatus(isActive ? undefined : key)}
          >
            {config.label}
          </Button>
        );
      })}
    </div>
  </div>
);

/** Single-establishment autocomplete stored as `establishmentId`. */
const EstablishmentFilter: React.FC<{
  filters: AssessmentFilters;
  onSetEstablishment: (id: string | undefined) => void;
}> = ({ filters, onSetEstablishment }) => {
  const { options, isLoading, onSearch, onLoadMore, hasMore } = useEstablishmentsAutocomplete();
  return (
    <div className="filter-group">
      <span className="filter-group-label">Établissement</span>
      <div className="w-64">
        <Autocomplete
          placeholder="Rechercher un établissement..."
          options={options}
          value={filters.establishmentId ?? ''}
          onChange={(val) => onSetEstablishment(val || undefined)}
          onSearch={onSearch}
          onLoadMore={onLoadMore}
          hasMore={hasMore}
          isLoading={isLoading}
          minSearchLength={2}
          inputSize="sm"
        />
      </div>
    </div>
  );
};

/** Debounced VSC / coordinator name search; empty string becomes `undefined`. */
const SearchFilter: React.FC<{
  filters: AssessmentFilters;
  onSetSearch: (value: string | undefined) => void;
}> = ({ filters, onSetSearch }) => (
  <div className="filter-group">
    <span className="filter-group-label">Recherche</span>
    <div className="w-56">
      <SearchInput
        size="sm"
        shortcut={false}
        debounceMs={300}
        placeholder="Nom du VSC, du coordinateur..."
        value={filters.search ?? ''}
        onSearch={(q) => onSetSearch(q.trim() || undefined)}
        onClear={() => onSetSearch(undefined)}
      />
    </div>
  </div>
);

/** Filters for assessments. */
export const AssessmentFiltersContent: React.FC<AssessmentFiltersContentProps> = ({
  filters,
  allowedFilterIds,
  visibleFilterIds,
  onSetType,
  onSetStatus,
  onSetEstablishment,
  onSetSearch,
}) => {
  const show = (id: string) => isVisible(id, allowedFilterIds, visibleFilterIds);

  return (
    <div className="filter-groups-container">
      {show('filter-search') && <SearchFilter filters={filters} onSetSearch={onSetSearch} />}
      {show('filter-type') && <TypeFilter filters={filters} onSetType={onSetType} />}
      {show('filter-status') && <StatusFilter filters={filters} onSetStatus={onSetStatus} />}
      {show('filter-establishment') && (
        <EstablishmentFilter filters={filters} onSetEstablishment={onSetEstablishment} />
      )}
    </div>
  );
};
